import React, { useState, useEffect, useMemo } from 'react';
import {
  Paper,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import dashboardService from '../services/dashboardService';
import { DEAL_STAGES } from '../constants/dealStages';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Stage colors mapping with dash patterns
const STAGE_COLORS = {
  'Backlog': { border: '#9C27B0', background: 'rgba(156, 39, 176, 0.1)', dash: [2, 2] },
  'Lead': { border: '#2196F3', background: 'rgba(33, 150, 243, 0.1)', dash: [3, 3] },
  'Qualified': { border: '#4CAF50', background: 'rgba(76, 175, 80, 0.1)', dash: [5, 5] },
  'Proposed': { border: '#FF9800', background: 'rgba(255, 152, 0, 0.1)', dash: [10, 5] }
};

const OpportunitiesByMonthChart = () => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [monthsForward, setMonthsForward] = useState(6);
  const [selectedStages, setSelectedStages] = useState({
    Backlog: false,
    Lead: false,
    Qualified: true,
    Proposed: true
  });

  useEffect(() => {
    loadChartData();
  }, [monthsForward, selectedStages]);

  const loadChartData = async () => {
    try {
      setLoading(true);
      setError('');
      // Convert selected stages to comma-separated string
      const activeStages = Object.entries(selectedStages)
        .filter(([_, isSelected]) => isSelected)
        .map(([stage]) => stage)
        .join(',');
      
      if (!activeStages) {
        setChartData([]);
        return;
      }

      const data = await dashboardService.getOpportunitiesByMonth(monthsForward, activeStages);
      setChartData(data);
    } catch (err) {
      console.error('Error loading opportunities by month:', err);
      setError('Failed to load chart data');
    } finally {
      setLoading(false);
    }
  };

  // Process data for the chart
  const processedChartData = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return {
        labels: [],
        datasets: [],
        monthlyData: []
      };
    }

    const labels = chartData.map(item => item.MonthLabel);
    const datasets = Object.entries(selectedStages)
      .filter(([_, isSelected]) => isSelected)
      .map(([stage]) => ({
        label: stage,
        data: chartData.map(item => parseFloat(item[`${stage}Value`] || 0)),
        borderColor: STAGE_COLORS[stage].border,
        backgroundColor: STAGE_COLORS[stage].background,
        borderWidth: 2,
        pointBackgroundColor: STAGE_COLORS[stage].border,
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderDash: STAGE_COLORS[stage].dash,
        fill: false,
        tension: 0.4
      }));

    const monthlyData = chartData.map(item => {
      const stageData = {};
      Object.entries(selectedStages)
        .filter(([_, isSelected]) => isSelected)
        .forEach(([stage]) => {
          stageData[`${stage.toLowerCase()}Value`] = parseFloat(item[`${stage}Value`] || 0);
          stageData[`${stage.toLowerCase()}Count`] = parseInt(item[`${stage}Count`] || 0);
        });

      return {
        month: item.MonthLabel,
        ...stageData,
        totalValue: Object.entries(selectedStages)
          .filter(([_, isSelected]) => isSelected)
          .reduce((sum, [stage]) => sum + parseFloat(item[`${stage}Value`] || 0), 0),
        totalCount: Object.entries(selectedStages)
          .filter(([_, isSelected]) => isSelected)
          .reduce((sum, [stage]) => sum + parseInt(item[`${stage}Count`] || 0), 0)
      };
    });

    return {
      labels,
      datasets,
      monthlyData
    };
  }, [chartData, selectedStages]);

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          padding: 20,
          usePointStyle: true,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          title: function(context) {
            return context[0].label;
          },
          label: function(context) {
            const dataIndex = context.dataIndex;
            const monthData = processedChartData.monthlyData[dataIndex];
            const formattedValue = new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'CAD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(context.parsed.y);

            if (context.datasetIndex === 0) {
              // Backlog
              return [
                `Backlog Value: ${formattedValue}`,
                `Backlog Opportunities: ${monthData.BacklogCount}`
              ];
            } else if (context.datasetIndex === 1) {
              // Lead
              return [
                `Lead Value: ${formattedValue}`,
                `Lead Opportunities: ${monthData.LeadCount}`
              ];
            } else if (context.datasetIndex === 2) {
              // Qualified
              return [
                `Qualified Value: ${formattedValue}`,
                `Qualified Opportunities: ${monthData.QualifiedCount}`
              ];
            } else {
              // Proposed
              return [
                `Proposed Value: ${formattedValue}`,
                `Proposed Opportunities: ${monthData.ProposedCount}`
              ];
            }
          }
        },
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.3)',
        borderWidth: 1,
        cornerRadius: 6,
        displayColors: true,
        padding: 12
      }
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)'
        },
        title: {
          display: true,
          text: 'Month',
          font: {
            size: 12,
            weight: 'bold'
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)'
        },
        title: {
          display: true,
          text: 'Value (CAD)',
          font: {
            size: 12,
            weight: 'bold'
          }
        },
        ticks: {
          callback: function(value) {
            return new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'CAD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
              notation: 'compact'
            }).format(value);
          }
        }
      }
    },
    interaction: {
      mode: 'index',
      intersect: false
    },
    onHover: (event, activeElements) => {
      event.native.target.style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      notation: 'compact'
    }).format(value);
  };

  const handleStageChange = (stage) => {
    setSelectedStages(prev => ({
      ...prev,
      [stage]: !prev[stage]
    }));
  };

  const totalValue = processedChartData.monthlyData?.reduce((sum, month) => sum + month.totalValue, 0) || 0;
  const totalOpportunities = processedChartData.monthlyData?.reduce((sum, month) => sum + month.totalCount, 0) || 0;

  if (loading) {
    return (
      <Paper sx={{ p: 3, height: 500 }}>
        <Typography variant="h6" gutterBottom>
          Opportunities Pipeline by Month
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <Typography>Loading...</Typography>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, height: 500 }}>
        <Typography variant="h6" gutterBottom>
          Opportunities Pipeline by Month
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, height: 500 }}>
      <Typography variant="h6" gutterBottom>
        Opportunities Pipeline by Month
      </Typography>

      {/* Controls Section */}
      <Box sx={{ mb: 3 }}>
        {/* Time Period and Stats Row */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          flexWrap: 'wrap', 
          gap: 2,
          mb: 2 
        }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Forecast Period</InputLabel>
            <Select
              value={monthsForward}
              onChange={(e) => setMonthsForward(e.target.value)}
              label="Forecast Period"
            >
              <MenuItem value={3}>Next 3 Months</MenuItem>
              <MenuItem value={6}>Next 6 Months</MenuItem>
              <MenuItem value={12}>Next 12 Months</MenuItem>
              <MenuItem value={24}>Next 24 Months</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              Total Opportunities: <strong>{totalOpportunities}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Pipeline Value: <strong>{formatCurrency(totalValue)}</strong>
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Stage Selection */}
        <FormGroup row sx={{ 
          gap: 2, 
          justifyContent: 'center',
          width: '100%'
        }}>
          {Object.entries(selectedStages).map(([stage, isSelected]) => (
            <FormControlLabel
              key={stage}
              control={
                <Checkbox
                  checked={isSelected}
                  onChange={() => handleStageChange(stage)}
                  sx={{
                    color: STAGE_COLORS[stage].border,
                    '&.Mui-checked': {
                      color: STAGE_COLORS[stage].border,
                    },
                  }}
                />
              }
              label={stage}
            />
          ))}
        </FormGroup>
      </Box>

      {/* Chart */}
      <Box sx={{ height: 300, position: 'relative' }}>
        {processedChartData.labels.length > 0 ? (
          <Line data={processedChartData} options={chartOptions} />
        ) : (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            color: 'text.secondary'
          }}>
            <Typography>
              {Object.values(selectedStages).some(v => v) 
                ? 'No opportunities found for selected stages'
                : 'Please select at least one stage to display data'}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default OpportunitiesByMonthChart; 